import { EntityId, OccupationTemplate, PlacesInfoResponseType, Restaurant } from '@restworld/utility-types';
import { ENDPOINTS } from './../../endpoints';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import { useMemo } from 'react';
import {
  ExploreRestaurantsGraphql,
  GetLocalities,
  IndexRestaurants,
  PopularLocality,
  RestaurantEmployee,
  RestaurantEmployeesParams,
  occupationTemplateParams,
  RestaurantFilterType,
  RestaurantLocationCoordinates,
  RestaurantLocationCoordinatesParams,
  ShowRestaurant,
  IRestaurantsPayload,
  RestaurantsNamesReturnType,
  RestaurantStatisticsResponse,
  RestaurantViewLog,
  CreateEmployerRestaurantPayload,
  OtherRestaurantValuesType,
} from './types';
import { UploadPicture } from '../../utils/common_types';
import useServices from '../../hooks/useServices';
import { exploreRestaurantsGraphqlQuery } from './graphqlQueries';
import { AxiosRequestConfig } from 'axios';

class ServiceRestaurants extends Service {
  fetchRestaurants({
    limit,
    offset,
    order_by,
    filters,
  }: {
    limit?: number;
    offset?: number;
    order_by?: string[];
    filters?: RestaurantFilterType;
  }) {
    return this.axiosInstance.get<IndexRestaurants>(ENDPOINTS.restaurants.root, {
      params: { limit, offset, order_by, ...filters },
    });
  }

  createRestaurant(params: Partial<CreateEmployerRestaurantPayload>) {
    return this.axiosInstance.post<{ id: string; slug?: string }>(ENDPOINTS.restaurants.root, params);
  }

  checkRestaurantDuplicate(params: { restaurant_name: string; address_lat: number; address_lon: number }) {
    return this.axiosInstance.post<{
      duplicate: boolean;
      restaurant_id?: string;
      organization_id?: string;
      restaurant_name?: string;
      locality?: string;
    }>(ENDPOINTS.restaurants.checkDuplicate, params);
  }

  fetchRestaurantIds({ showDetails, limit }: { showDetails: boolean; limit?: number }) {
    return this.axiosInstance.get<{ id: string; slug: string }[]>(ENDPOINTS.restaurants.restaurantIds, {
      params: { show_details: showDetails, limit },
    });
  }

  showRestaurant(restaurantId: EntityId) {
    return this.axiosInstance.get<ShowRestaurant>(ENDPOINTS.restaurants.root + '/' + restaurantId);
  }

  showPublicRestaurant(restaurantId: string) {
    return this.axiosInstance.get<ShowRestaurant>(ENDPOINTS.restaurants.publicRestaurant + '/' + restaurantId);
  }

  updateRestaurant({ restaurantId, params }: { restaurantId: EntityId; params: IRestaurantsPayload }) {
    return this.axiosInstance.put<string>(ENDPOINTS.restaurants.root + '/' + restaurantId, params);
  }

  updateRestaurantOwnerPicture({ restaurantId, file }: { restaurantId: EntityId; file: File }) {
    const formData = new FormData();
    formData.append('owner_profile_picture', file);

    return this.axiosInstance.put<string>(ENDPOINTS.restaurants.root + '/' + restaurantId, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  uploadPicture({
    restaurantId,
    file, // Defines the position of the new picture, either it can be the first or the last of existing pictures
    position = 'last',
  }: {
    restaurantId: EntityId;
    file: File | File[];
    position?: 'first' | 'last';
  }) {
    const formData = new FormData();
    formData.append('id', restaurantId);
    if (Array.isArray(file)) {
      file?.forEach((image) => {
        formData.append('picture[]', image);
      });
    } else {
      formData.append('picture', file);
    }
    formData.append('position', position);

    return this.axiosInstance.post<UploadPicture>(ENDPOINTS.restaurants.picture, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  // using the same endpoint with different parameters
  uploadRestaurantLogo({ restaurantId, file }: { restaurantId: EntityId; file: File }) {
    const formData = new FormData();
    formData.append('id', restaurantId);
    formData.append('restaurant_logo', file);

    return this.axiosInstance.post<UploadPicture>(ENDPOINTS.restaurants.picture, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  deletePicture({ restaurantId, pictureUrl }: { restaurantId: EntityId; pictureUrl: string }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.restaurants.picture, {
      data: { id: restaurantId, url: pictureUrl },
    });
  }

  fetchLocalities({ organizationId }: { organizationId?: string }) {
    return this.axiosInstance.get<GetLocalities>(ENDPOINTS.restaurants.localities, {
      params: { organization_id: organizationId },
    });
  }

  fetchRestaurantNames({ organization_id }: { organization_id?: string }) {
    return this.axiosInstance.get<RestaurantsNamesReturnType[]>(ENDPOINTS.restaurants.restaurantNames, {
      params: { organization_id },
    });
  }

  restaurantPlacesInfo(restaurant_id: string) {
    return this.axiosInstance.get<PlacesInfoResponseType>(ENDPOINTS.restaurants.gMapPlaces, {
      params: {
        restaurant_id,
      },
    });
  }

  fetchPopularLocalities(limit?: number) {
    return this.axiosInstance.get<PopularLocality[]>(ENDPOINTS.restaurants.popularLocalities, {
      params: { limit },
    });
  }

  async fetchExploreRestaurantsList(
    params: {
      limit?: number;
      offset?: number;
      lat?: number;
      lon?: number;
      radius?: number;
    },
    config?: AxiosRequestConfig
  ) {
    const r = await this.axiosInstance.post<ExploreRestaurantsGraphql>(
      ENDPOINTS.employers.graphql,
      {
        query: exploreRestaurantsGraphqlQuery,
        variables: { ...params },
      },
      {
        ...config,
      }
    );
    return r;
  }

  getRestaurantLocationCoordinates(params?: RestaurantLocationCoordinatesParams) {
    return this.axiosInstance.get<RestaurantLocationCoordinates[]>(
      ENDPOINTS.restaurants.restaurantLocationCoordinates,
      {
        params,
      }
    );
  }

  fetchRestaurantEmployees(restaurantId?: EntityId) {
    return this.axiosInstance.get<{ data: RestaurantEmployee[] }>(ENDPOINTS.restaurants.members, {
      ...(restaurantId ? { params: { restaurant_id: restaurantId } } : {}),
    });
  }

  createRestaurantEmployee(params: RestaurantEmployeesParams) {
    const formData = new FormData();
    formData.append('restaurant_id', params.restaurant_id);
    formData.append('full_name', params.full_name);
    if (params.occupation) formData.append('occupation', params.occupation);
    if (params.picture) formData.append('picture', params.picture);
    if (params.age) formData.append('age', params.age.toString());
    if (params.short_presentation) formData.append('short_presentation', params.short_presentation);

    return this.axiosInstance.post<{ data: RestaurantEmployee }>(ENDPOINTS.restaurants.employees, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  getRestaurantEmployeesById(restaurantEmployeeId: EntityId) {
    return this.axiosInstance.get<{ data: RestaurantEmployee }>(
      ENDPOINTS.restaurants.employees + '/' + restaurantEmployeeId
    );
  }

  updateRestaurantEmployee({
    restaurantEmployeeId,
    params,
  }: {
    restaurantEmployeeId: EntityId;
    params: Partial<RestaurantEmployeesParams>;
  }) {
    const formData = new FormData();
    if (params.restaurant_id) formData.append('restaurant_id', params.restaurant_id);
    if (params.full_name) formData.append('full_name', params.full_name);
    if (params.occupation) formData.append('occupation', params.occupation);
    if (params?.picture) formData.append('picture', params?.picture ?? '');
    if (params?.age) formData.append('age', params?.age?.toString() ?? '');
    if (params?.short_presentation) formData.append('short_presentation', params?.short_presentation ?? '');

    return this.axiosInstance.put<{ data: RestaurantEmployee }>(
      ENDPOINTS.restaurants.employees + '/' + restaurantEmployeeId,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      }
    );
  }

  deleteRestaurantEmployee(restaurantEmployeeId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.restaurants.employees + '/' + restaurantEmployeeId);
  }

  createOccupationTemplate(params: occupationTemplateParams) {
    return this.axiosInstance.post<{ data: OccupationTemplate }>(ENDPOINTS.restaurants.occupationTemplates, params);
  }

  fetchOccupationTemplates(restaurantId: EntityId) {
    return this.axiosInstance.get<{ data: OccupationTemplate[] }>(ENDPOINTS.restaurants.occupationTemplates, {
      params: { restaurant_id: restaurantId },
    });
  }

  updateOccupationTemplate({
    occupationTemplateId,
    params,
  }: {
    occupationTemplateId: EntityId;
    params: Partial<occupationTemplateParams>;
  }) {
    return this.axiosInstance.put<{ data: OccupationTemplate }>(
      ENDPOINTS.restaurants.occupationTemplates + '/' + occupationTemplateId,
      {
        ...params,
        description: params.description ?? '',
      }
    );
  }

  deleteOccupationTemplate(occupationTemplateId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.restaurants.occupationTemplates + '/' + occupationTemplateId);
  }

  fetchOccupationTemplate(occupationTemplateId: EntityId) {
    return this.axiosInstance.get<{ data: OccupationTemplate }>(
      ENDPOINTS.restaurants.occupationTemplates + '/' + occupationTemplateId
    );
  }

  showViolaBadge(jobPositionId: EntityId) {
    return this.axiosInstance.get<boolean>(ENDPOINTS.restaurants.violaBadge, {
      params: { job_position_id: jobPositionId },
    });
  }

  setSeoData({
    restaurant_id,
    meta_description,
    meta_title,
    input_meta_image,
  }: {
    restaurant_id: string;
    input_meta_image?: File;
    meta_description?: string;
    meta_title?: string;
  }) {
    const formData = new FormData();
    formData.append('id', restaurant_id);
    if (input_meta_image) formData.append('input_meta_image', input_meta_image);
    if (meta_description) formData.append('meta_description', meta_description);
    if (meta_title) formData.append('meta_title', meta_title);

    return this.axiosInstance.post<Restaurant>(`${ENDPOINTS.restaurants.root}/seo_metadata`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  associateNewReferentContact({
    restaurantId,
    employerUserContactId,
  }: {
    restaurantId?: string;
    employerUserContactId?: string | string[];
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.restaurants.referentContact, {
      restaurant_id: restaurantId,
      employer_user_contact_id: employerUserContactId,
    });
  }

  removeReferentContactAssociation({
    restaurantId,
    employerUserContactId,
  }: {
    restaurantId?: string;
    employerUserContactId?: string;
  }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.restaurants.referentContact, {
      data: { restaurant_id: restaurantId, employer_user_contact_id: employerUserContactId },
    });
  }

  createRestaurantViewLog({
    restaurantId,
    ambassadorCode,
    utmSource,
  }: {
    restaurantId: string;
    ambassadorCode?: string;
    utmSource?: string;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.restaurants.restaurantViewLog, {
      restaurant_id: restaurantId,
      ambassador_code: ambassadorCode,
      utm_source: utmSource,
    });
  }

  // This doesnt return view logs, but the utm grouped data, which contains view and spontaneous_application counts
  getRestaurantViewLog({
    restaurantId,
    durationValue,
    durationUnit,
  }: {
    restaurantId: string;
    durationValue?: number;
    durationUnit?: 'days' | 'months';
  }) {
    return this.axiosInstance.get<RestaurantViewLog>(`${ENDPOINTS.restaurants.statistics}/utm_source`, {
      params: { restaurant_id: restaurantId, duration_value: durationValue, duration_unit: durationUnit },
    });
  }

  fetchRestaurantStatistics({
    restaurant_id,
    type,
    duration_value,
    duration_unit,
  }: {
    restaurant_id: string;
    type?: 'chart' | undefined;
    duration_value?: number;
    duration_unit?: 'days' | 'months';
  }) {
    // given type="chart" would return chart data as RestaurantChartStatistics type
    return this.axiosInstance.get<RestaurantStatisticsResponse>(ENDPOINTS.restaurants.statistics, {
      params: { restaurant_id, type, duration_value, duration_unit },
    });
  }

  getAdditionalRestaurantOnboardingData({
    gmaps_place_id,
    restaurant,
  }: {
    gmaps_place_id: string;
    restaurant?: string;
  }) {
    return this.axiosInstance.get<Restaurant>(ENDPOINTS.restaurants.additionalOnboardingData, {
      params: {
        gmaps_place_id,
        ...(restaurant ? { restaurant } : {}),
      },
    });
  }

  getOtherRestaurantValues({ organizationId }: { organizationId?: string }) {
    return this.axiosInstance.get<OtherRestaurantValuesType[]>(ENDPOINTS.restaurants.otherRestaurantValues, {
      params: { organization_id: organizationId },
    });
  }

  /**
   * Public endpoint: check if a restaurant already exists by gmaps_place_id or by name + coordinates.
   */
  checkRestaurantExistence(
    params: { gmaps_place_id: string } | { restaurant_name: string; address_lat: number; address_lon: number }
  ) {
    return this.axiosInstance.post<{
      exists: boolean;
      restaurant_name?: string;
      organization_id?: string;
    }>(ENDPOINTS.restaurants.checkExistence, params);
  }
}

export const createRestaurantsService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) =>
  new ServiceRestaurants(dynamicAxios(baseUrl, jwt));

export function useRestaurantsService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceRestaurants(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useRestaurantsService;

export * from './types';
